import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getAllASnStatusData,
  fetchBoxNumberByVendor,
  uploadBillDetailsAPI,
} from "Service/ApiService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IconButton, TextField, Tooltip } from "@mui/material";
import { useLoading } from "context/LoadingContext";
import { useAlert } from "../context/AlertContext";
import PropTypes from "prop-types";
import StatusCell from "./StatusCell";
import MDTypography from "components/MDTypography";
import { DataArrayRounded, Edit, RemoveRedEye, Stairs } from "@mui/icons-material";
import MDInput from "components/MDInput";

// Define validation schema
const schema = Yup.object().shape({
  billNumber: Yup.string().required("Field is required"),
  invoiceDate: Yup.string().required("Field is required"),
  ewayBillNumber: Yup.string().required("Field is required"),
  deliveryDate: Yup.string().required("Field is required"),
});

const AsnStatusController = () => {
  const [asnStatus, setASNSTatusData] = useState([]);
  const { setLoading } = useLoading();
  const { showAlert } = useAlert();
  const [openPopup, setOpenPopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const [searchPONumber, setSearchPONumber] = useState("");

  const [editPONumber, setEditPONumber] = useState("");
  const [editData, setEditData] = useState([]);

  // pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 200 });
  const [rowCount, setRowCount] = useState(0);
  const vendordetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendordetails ? vendordetails.vendorCode : null;

  const [billData, setBillData] = useState([]);
  const [errors, setErrors] = useState({});
  const billSchema = Yup.object().shape({
    boxNumber: Yup.string().required(),
    billNumber: Yup.string().required("Invoice Number is required"),
    invoiceDate: Yup.string().required("Invoice Date is required"),
    ewayBillNumber: Yup.string().required("E-Way Bill Number is required"),
    deliveryDate: Yup.string().required("Delivery Date is required"),
  });

  // Validation
  const validateField = async (boxNumber, fieldName, value) => {
    try {
      await Yup.reach(schema, fieldName).validate(value);

      setErrors((prev) => ({
        ...prev,
        [boxNumber]: {
          ...prev[boxNumber],
          [fieldName]: null, // clear error if valid
        },
      }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [boxNumber]: {
          ...prev[boxNumber],
          [fieldName]: err.message, // set error for this box + field
        },
      }));
    }
  };

  useEffect(() => {
    getVendorStatus();
  }, [paginationModel, searchPONumber]);

  const getVendorStatus = async () => {
    try {
      // setLoading(true);
      setASNSTatusData([]);
      const response = await getAllASnStatusData(
        vendorCode,
        searchPONumber,
        paginationModel.page,
        paginationModel.pageSize
      ); // Assuming this function fetches orders data from the API

      if (response && response.success === "true") {
        setASNSTatusData(response.ASNStatusList);
        setRowCount(response.rowCount);
      }
      // setLoading(false);
    } catch (error) {
      // setLoading(false);
    }
  };

  const DownloadButtonCell = ({ row }) => (
    <IconButton onClick={() => hangetstagesgroups.phpdleDownloadClick(row)} color="success">
      <DownloadIcon />
    </IconButton>
  );

  DownloadButtonCell.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          {/* Download */}
          <Tooltip title="Download">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleDownloadClick(params.row)}
            >
              <DownloadIcon fontSize="medium" />
            </IconButton>
          </Tooltip>

          {/* View */}
          <Tooltip title="View">
            <IconButton size="small" color="info" onClick={() => handleViewClick(params.row)}>
              <RemoveRedEye fontSize="medium" />
            </IconButton>
          </Tooltip>

          {/* Edit (Only if Accepted) */}
          {params.row.approve === "Accepted" && (
            <Tooltip title="Edit Bill Details">
              <IconButton size="small" color="warning" onClick={() => handleEditClick(params.row)}>
                <Edit fontSize="medium" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: "poNumber",
      headerName: "PO Number",
      flex: 1,
    },
    {
      field: "poDate",
      headerName: "PO Date",
      flex: 1,
      sortable: true,
      filterable: true,

      // Sorting & filtering ke liye correct Date object return karna zaroori hai
      valueGetter: (params) => (params.row.poDate ? new Date(params.row.poDate) : null),

      // UI me original format return karna (yyyy-mm-dd)
      valueFormatter: (params) => {
        if (!params.value) return "";
        const d = params.value;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`; // 👈 yahi format show hoga
      },
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      type: "string",
      valueGetter: (params) => params.row.city || "",
      sortable: true,
      filterable: true,
    },

    {
      field: "approve",
      headerName: "Status",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = params.value;

        const color = status === "Accepted" ? "green" : status === "Rejected" ? "red" : "#ffaa00"; // Pending / Others

        return (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              color: "black",
              fontFamily: "bold",
              backgroundColor: color,
              fontSize: "12px",
            }}
          >
            {status}
          </span>
        );
      },
    },

    {
      field: "remark",
      headerName: "Remark",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.items?.[0]?.remark || ""}>
          <MDTypography
            variant="light"
            noWrap
            sx={{
              display: "inline-block",
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.row.items?.[0]?.remark}
          </MDTypography>
        </Tooltip>
      ),
    },
    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   width: 150,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params) => (
    //     <>
    //       {/* View Details */}
    //       <Tooltip title="View">
    //         <IconButton size="small" color="info" onClick={() => handleViewClick(params.row)}>
    //           <RemoveRedEye fontSize="medium" />
    //         </IconButton>
    //       </Tooltip>

    //       {/* Edit Bill Details */}
    //       {params.row.approve === "Accepted" && (
    //         <Tooltip title="Edit Bill Details">
    //           <IconButton size="small" color="warning" onClick={() => handleEditClick(params.row)}>
    //             <Edit fontSize="medium" />
    //           </IconButton>
    //         </Tooltip>
    //       )}
    //     </>
    //   ),
    // },
  ];

  const materialcolumns = [
    { accessor: "materialCode", Header: "Material Code", hidden: true },
    { accessor: "boxNumber", Header: "Box Number", hidden: true },
    { accessor: "brand", Header: "Brand", hidden: true },
    { accessor: "product", Header: "Product", hidden: true },
    { accessor: "invoiceNumber", Header: "Invoice Number", hidden: true },
    { accessor: "invoiceDate", Header: "Invoice Date", hidden: true },
    { accessor: "materialDesc", Header: "Material Desc", hidden: true },
    { accessor: "barcode", Header: "Barcode", hidden: true },
    { accessor: "quantity", Header: "quantity", hidden: true },
  ];

  const generateMaterialRows = (dataList) => {
    if (!selectedData) return [];

    return dataList
      .filter((data) => data.poNumber === selectedData.poNumber)
      .map((data) => ({
        id: data.id,
        materialCode: data.materialCode,
        boxNumber: data.boxNumber,
        materialDesc: data.materialDesc,
        brand: data.brand,
        product: data.product,
        invoiceNumber: data.InvoiceNumber,
        invoiceDate: data.InvoiceDate,
        barcode: data.barcode,
        quantity: data.quantity,
      }));
  };

  const handleDownloadClick = async (row) => {
    const materials = asnStatus.filter((item) => item.poNumber === row.poNumber);

    // Combined Header
    const sheetHeader = [
      "PO Number",
      "PO Date",
      "City",
      "Material Code",
      "Brand",
      "Product",
      "Material Desc",
      "Barcode",
      "Quantity",
    ];

    // Combined Data Rows
    const sheetRows = materials.map((item) => [
      row.poNumber,
      row.poDate,
      row.city,
      item.materialCode,
      item.brand,
      item.product,
      item.materialDesc,
      item.barcode,
      item.quantity,
    ]);

    const sheetData = [sheetHeader, ...sheetRows];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Style the header row
    for (let C = 0; C < sheetHeader.length; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "ffffff" } },
          fill: { fgColor: { rgb: "4CAF50" } },
          alignment: { horizontal: "center" },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ASN Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `ASN_Report_${row.poNumber}.xlsx`);
  };

  const handleViewClick = (row) => {
    setSelectedData(row);
    setOpenPopup(true);
  };

  const generateRows = (dataList) => {
    const grouped = {};

    dataList.forEach((data) => {
      console.log(data.poNumber);
      const groupKey = `${data.poNumber}-${data.vendorCode}`;
      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          id: data.id,
          poNumber: data.poNumber,
          poDate: data.poDate,
          city: data.city,
          approve: data.approve == "1" ? "Accepted" : data.approve == "2" ? "Rejected" : "Pending",
          createdAt: data.createdAt,
          items: [],
        };
      }

      // Add individual row to the group (if you want to retain detail list)
      grouped[groupKey].items.push(data);
    });

    // Return as array
    return Object.values(grouped);
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedData(null);
  };

  // Edit modal
  const handleEditClick = (row) => {
    // MUI DataGrid passes the row data directly as `params.row`.
    // Some table libs pass `row.original`; accept both.
    const poNumberValue = row?.original?.poNumber ?? row?.poNumber;
    if (!poNumberValue) {
      return; // nothing to edit
    }
    const poNumber = String(poNumberValue);
    setEditPONumber(poNumber);
    getEditData(poNumber);
    setOpenEditPopup(true);
  };

  const handleCloseEditPopup = () => {
    setOpenEditPopup(false);
    setEditData([]);
  };

  // Edit table columns
  const editColumns = [
    { accessor: "boxNumber", Header: "Box Number" },
    {
      accessor: "billNumber",
      Header: "Invoice Number",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        return (
          /* eslint-disable react/prop-types */
          <TextField
            defaultValue={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.billNumber
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => updateBillNumber(row.original.boxNumber, e.target.value)}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.billNumber}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.billNumber || ""}
          ></TextField>
        );
      },
    },
    {
      accessor: "invoiceDate",
      Header: "Invoice Date",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        return (
          <MDInput
            type="date"
            value={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.invoiceDate
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => updateInvoiceDateNumber(row.original.boxNumber, e.target.value)}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.invoiceDate}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.invoiceDate || ""}
            sx={{
              "& .MuiInputBase-root": {
                border: !!errors[row.original.boxNumber]?.invoiceDate ? "1px solid red" : "",
                borderRadius: 1,
              },
              "& .MuiFormHelperText-root": {
                color: "red",
              },
            }}
          />
        );
      },
    },
    {
      accessor: "ewayBillNumber",
      Header: "E-Way Bill Number",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        return (
          // eslint-disable-next-line react/prop-types
          <TextField
            defaultValue={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.ewayBillNumber
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => updateEWayBillNumber(row.original.boxNumber, e.target.value)}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.ewayBillNumber}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.ewayBillNumber || ""}
          ></TextField>
        );
      },
    },
    {
      accessor: "deliveryDate",
      Header: "Delivery Date",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        return (
          <MDInput
            type="date"
            InputLabelProps={{ shrink: true }}
            value={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.deliveryDate
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => updateDeliveryDateNumber(row.original.boxNumber, e.target.value)}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.deliveryDate}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.deliveryDate || ""}
            sx={{
              "& .MuiInputBase-root": {
                border: !!errors[row.original.boxNumber]?.deliveryDate ? "1px solid red" : "",
                borderRadius: 1,
              },
              "& .MuiFormHelperText-root": {
                color: "red",
              },
            }}
          />
        );
      },
    },
  ];

  // Edit rows
  const getEditData = async (poNumber) => {
    try {
      setLoading(true);
      setEditData([]);
      const response = await fetchBoxNumberByVendor(poNumber, vendorCode); // Using the passed in value
      if (response && response.success === "true") {
        // Ensure that response.data is an array
        setEditData(Array.isArray(response.data) ? response.data : [response.data]);

        const bills = response.data.map((item) => ({
          boxNumber: item.boxNumber,
          billNumber: item.InvoiceNumber,
          invoiceDate: item.InvoiceDate,
          ewayBillNumber: item.ewayBillNumber,
          deliveryDate: item.deliveryDate,
        }));

        const billDetails = {
          vendorCode: vendorCode,
          PONumber: poNumber,
          bills: bills,
        };

        setBillData(billDetails);
      } else {
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  // updater e way bill function
  const updateInvoiceDateNumber = (boxNumber, value) => {
    validateField(boxNumber, "invoiceDate", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, invoiceDate: value } : bill
        ),
      };
    });
  };

  // updater bill function
  const updateBillNumber = (boxNumber, value) => {
    validateField(boxNumber, "billNumber", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, billNumber: value } : bill
        ),
      };
    });
  };

  // updater e way bill function
  const updateEWayBillNumber = (boxNumber, value) => {
    validateField(boxNumber, "ewayBillNumber", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, ewayBillNumber: value } : bill
        ),
      };
    });
  };

  // updater e way bill function
  const updateDeliveryDateNumber = (boxNumber, value) => {
    validateField(boxNumber, "deliveryDate", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, deliveryDate: value } : bill
        ),
      };
    });
  };

  const uploadBillDetails = async () => {
    const newErrors = {};
    billData.bills.forEach((bill) => {
      const fieldErrors = {};
      if (!bill.boxNumber) fieldErrors.boxNumber = "Box Number is required";
      if (!bill.billNumber) fieldErrors.billNumber = "Invoice Number is required";
      if (!bill.ewayBillNumber) fieldErrors.ewayBillNumber = "E-Way Bill Number is required";
      if (!bill.deliveryDate) fieldErrors.deliveryDate = "Delivery Date is required";
      if (!bill.invoiceDate) fieldErrors.invoiceDate = "Invoice Date is required";

      newErrors[bill.boxNumber || `temp-${Math.random()}`] = fieldErrors;
    });

    setErrors(newErrors);

    // Check if any field error exists
    const hasErrors = Object.values(newErrors).some((errorObj) =>
      Object.values(errorObj).some((errorMsg) => errorMsg)
    );

    if (hasErrors) {
      return;
    }

    try {
      setLoading(true);
      const response = await uploadBillDetailsAPI(billData); // Using the passed in value
      if (response && response.success === true) {
        showAlert("Bill Details Uploaded Successfully!", {
          icon: "success",
          title: "Successfully Uploaded",
        });
        handleCloseEditPopup();
      } else {
        showAlert("Bill Details Not Uploaded!");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return {
    asnStatus,
    paginationModel,
    rowCount,
    setPaginationModel,
    handleSelectionChange,
    generateRows,
    materialcolumns,
    generateMaterialRows,
    columns,
    handleClosePopup,
    openPopup,
    selectedData,

    editData,
    openEditPopup,
    handleEditClick,
    handleCloseEditPopup,
    editColumns,
    getEditData,
    uploadBillDetails,

    searchPONumber,
    setSearchPONumber,
  };
};

export default AsnStatusController;
